import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useNavigate } from '@lib/react-router-dom'

import { WalletBalancesOptionsContent } from '@pages/Traders-Room/Balances/Wallets/WalletBalancesOptions'

import { useModal } from '@context/ModalsContext'

import { AlertIconWrapper } from '@ui/AlertIconWrapper/AlertIconWrapper'
import { Body2, Heading4 } from '@ui/Typography'
import { Button, IconButton } from '@ui/components/Button'
import { Link } from '@ui/components/Link'
import { Tooltip } from '@ui/components/Tooltip/Tooltip'
import { DummyIcon } from '@ui/icons/Dummy.icon'
import { KebabIcon } from '@ui/icons/Kebab.icon'
import { WarningIcon } from '@ui/icons/Warning.icon'

import { CommonPaths } from '@utils/path'
import { WalletRestrictions, isRestricted, isWalletSuspended } from '@utils/wallet.utils'

import { WalletDto } from '@model/WalletDto'

import { useFormatNumber } from '@hooks/useFormatNumber'
import { useFundingNav } from '@hooks/useFundingNav'

import { Dropdown, DropdownCard, DropdownList } from '@shared/components/Dropdown'
import { SupportModal } from '@shared/modals/Support.modal'

import { useDepositTracker } from '@analytics/amplitude/deposit'

import styles from './WalletBalancesCard.module.scss'

interface WalletBalancesCardProps {
  wallet: WalletDto
  onWithdrawal: (walletId: string) => void
}

export const WalletBalancesCard: React.FC<WalletBalancesCardProps> = (props) => {
  const { wallet, onWithdrawal } = props
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const tracker = useDepositTracker()

  const { t } = useTranslation()

  const { formatMoney } = useFormatNumber()
  const navigate = useNavigate()
  const { navigateToFunding } = useFundingNav()
  const restrictionModal = useModal(SupportModal, {
    title: t('Temporarily unavailable'),
    description: t('Please contact Support for assistance.'),
  })

  const hasAllRestrictions = isWalletSuspended(wallet.restrictions)
  const isDepositRestricted = isRestricted(WalletRestrictions.DEPOSIT, wallet.restrictions)
  const isTransferFromRestricted = isRestricted(
    WalletRestrictions.TRANSFER_FROM,
    wallet.restrictions
  )

  return (
    <div className={styles.card}>
      <div className={styles.titleWrapper}>
        <IconButton.Ghost
          icon={hasAllRestrictions ? WarningIcon : DummyIcon}
          onClick={hasAllRestrictions ? () => restrictionModal.open() : undefined}
        />
        <Body2>
          {wallet.currency.id} {t('Wallet.Wallet')}
        </Body2>
        <Dropdown
          direction='topRight'
          distance='0.5rem'
          isOpen={dropdownOpen}
          setIsOpen={setDropdownOpen}
          content={
            <DropdownCard>
              <DropdownList>
                <WalletBalancesOptionsContent
                  wallet={wallet}
                  onWithdrawal={onWithdrawal}
                  close={() => setDropdownOpen(false)}
                />
              </DropdownList>
            </DropdownCard>
          }
        >
          <IconButton.Ghost size='md' icon={KebabIcon} onClick={() => setDropdownOpen(true)} />
        </Dropdown>
      </div>
      <Heading4 className={styles.title}>
        {formatMoney(wallet.balance, wallet.currency.id)}
      </Heading4>
      <div>
        <Body2 className={styles.subLabel}>{t('Wallet.Reserved')}</Body2>
        <Body2 medium>{formatMoney(wallet.reserved, wallet.currency.id)}</Body2>
      </div>
      <div className='mt-4 mb-1'>
        <AlertIconWrapper fullWidth spaced isActive={!hasAllRestrictions && isDepositRestricted}>
          <Tooltip
            fullWidth
            content={t('This action is currently unavailable.')}
            disabled={!isDepositRestricted}
          >
            <Button.Primary
              size='md'
              fullWidth
              disabled={isDepositRestricted}
              onClick={() => {
                tracker.start({ entry_point: 'balances', preset_wallet_id: wallet.id })
                navigateToFunding(`${CommonPaths.TradersRoomWallets}/${wallet.id}/deposit`)
              }}
            >
              {t('Deposit')}
            </Button.Primary>
          </Tooltip>
        </AlertIconWrapper>
      </div>
      <div className='mb-4'>
        <AlertIconWrapper
          fullWidth
          spaced
          isActive={!hasAllRestrictions && isTransferFromRestricted}
        >
          <Tooltip
            fullWidth
            content={t('This action is currently unavailable.')}
            disabled={!isTransferFromRestricted}
          >
            <Button.Secondary
              size='md'
              fullWidth
              disabled={isTransferFromRestricted}
              onClick={() => navigate(`${CommonPaths.TradersRoomWallets}/${wallet.id}/transfer`)}
            >
              {t('Transfer')}
            </Button.Secondary>
          </Tooltip>
        </AlertIconWrapper>
      </div>
      <div>
        <Link to='/dashboard/traders-room/transaction-history' state={{ walletId: wallet.id }}>
          {t('View Transaction History')}
        </Link>
      </div>
    </div>
  )
}
